import { computed, inject, Injectable } from '@angular/core';
import { AccessToken, decodeToken, IDToken, RefreshToken } from '@okta/okta-auth-js';
import { PortalTypeService, TokenTransferData } from './multi-tenant-auth';
import { OKTA_AUTH } from './okta-auth.token';
import { WINDOW } from './window.token';
import { from, Observable } from 'rxjs';
import { ENV } from './env.token';
import { b64DecodeUnicode } from '@shared/utils/base64';

/**
 * Token Receiver Service
 *
 * This service handles receiving tokens transferred from the central auth hub
 * via URL hash fragment. The auth hub must exchange the OAuth code for tokens
 * (it has the PKCE verifier), then transfers tokens to the tenant portal.
 *
 * Hybrid OAuth Flow:
 * 1. Auth hub initiates OAuth flow with Okta
 * 2. Okta redirects to auth hub with authorization code
 * 3. Auth hub exchanges code for tokens (has PKCE verifier)
 * 4. Auth hub redirects to tenant with tokens in hash: #tokens=<json>
 * 5. Tenant portal validates and stores tokens (this service)
 *
 * This should be called early in the application lifecycle
 * (e.g., via provideAppInitializer or the root component).
 */
@Injectable({
  providedIn: 'root',
})
export class TokenReceiverService {
  private readonly env = inject(ENV);
  private readonly oktaAuth = inject(OKTA_AUTH);
  private readonly window = inject(WINDOW);
  private readonly portalTypeService = inject(PortalTypeService);

  private hasProcessedTokens = false;

  private readonly scopes = computed(() => {
    const portalType = this.portalTypeService.getPortalTypeFromCurrentPath();
    switch (portalType) {
      case 'admin':
        return this.env.alliantPortalOkta.scopes;
      case 'student':
        return this.env.studentPortalOkta.scopes;
      case 'institution':
        return this.env.institutionPortalOkta.scopes;
      default:
        return ['openid', 'profile', 'email'];
    }
  });

  /**
   * Check for and process tokens from URL hash.
   * This should be called once during app initialization.
   *
   * @returns An Observable that emits a boolean indicating if tokens were found and processed
   */
  receiveTokensFromHash(): Observable<boolean> {
    return from(this.processTokensFromHash());
  }

  /**
   * Process tokens transferred from auth hub via URL hash.
   * Validates tokens before storing them.
   *
   * @returns A promise that resolves to a boolean indicating if tokens were found and processed
   */
  private async processTokensFromHash(): Promise<boolean> {
    // Only process once per page load
    if (this.hasProcessedTokens) {
      return false;
    }

    const tokenData = this.extractTokensFromWindowUrl();

    if (!tokenData) {
      return false;
    }

    this.hasProcessedTokens = true;

    try {
      // Validate token structure and claims
      const validationResult = await this.validateTokens(tokenData);
      if (!validationResult.isValid) {
        console.error('Token validation failed:', validationResult.error);
        this.clearTokenHash();
        return false;
      }

      const tokens = this.buildOktaTokens(tokenData);

      this.oktaAuth.tokenManager.setTokens(tokens);

      return true;
    } catch (error) {
      console.error('Error processing tokens from hash:', error);
      return false;
    } finally {
      this.clearTokenHash();
    }
  }

  /**
   * Validate tokens received from the auth hub.
   * Verifies token signatures, expiration, issuer, and audience.
   */
  private async validateTokens(tokenData: TokenTransferData): Promise<{ isValid: boolean; error?: string }> {
    const issuer = this.oktaAuth.options.issuer;
    const clientId = this.oktaAuth.options.clientId;

    const idTokenClaims = decodeToken(tokenData.idToken).payload;
    const accessTokenClaims = decodeToken(tokenData.accessToken).payload;

    if (!idTokenClaims.sub || !accessTokenClaims.sub) {
      return { isValid: false, error: 'Invalid token structure' };
    }

    if (issuer && idTokenClaims.iss !== issuer) {
      return { isValid: false, error: `Invalid issuer. Expected: ${issuer}, Got: ${idTokenClaims.iss}` };
    }

    const idTokenAud = Array.isArray(idTokenClaims.aud) ? idTokenClaims.aud : [idTokenClaims.aud];
    if (clientId && !idTokenAud.includes(clientId)) {
      return { isValid: false, error: `Invalid audience. Expected: ${clientId}, Got: ${idTokenClaims.aud}` };
    }

    const now = Math.floor(Date.now() / 1000);
    const clockSkewTolerance = 300;

    if (idTokenClaims.exp && idTokenClaims.exp + clockSkewTolerance < now) {
      return { isValid: false, error: 'ID token has expired' };
    }
    if (accessTokenClaims.exp && accessTokenClaims.exp + clockSkewTolerance < now) {
      return { isValid: false, error: 'Access token has expired' };
    }

    try {
      await this.oktaAuth.token.verify(
        {
          idToken: tokenData.idToken,
          claims: idTokenClaims,
          expiresAt: idTokenClaims.exp ?? 0,
          scopes: this.scopes(),
          authorizeUrl: '',
          issuer: idTokenClaims.iss ?? '',
          clientId: idTokenAud[0]?.toString() ?? '',
        },
        { issuer, clientId }
      );
    } catch (verifyError) {
      return {
        isValid: false,
        error: `ID token signature verification failed: ${verifyError instanceof Error ? verifyError.message : 'Unknown error'}`,
      };
    }

    return { isValid: true };
  }

  /**
   * Build Okta token objects from transfer data.
   */
  private buildOktaTokens(tokenData: TokenTransferData): {
    accessToken: AccessToken;
    idToken: IDToken;
    refreshToken?: RefreshToken;
  } {
    const accessTokenClaims = decodeToken(tokenData.accessToken).payload;
    const idTokenClaims = decodeToken(tokenData.idToken).payload;

    const scopes = this.extractScopes(accessTokenClaims);

    const accessToken: AccessToken = {
      accessToken: tokenData.accessToken,
      claims: accessTokenClaims,
      tokenType: 'Bearer',
      expiresAt: tokenData.expiresAt ?? Math.floor(Date.now() / 1000) + 3600,
      scopes,
      authorizeUrl: '',
      userinfoUrl: '',
    };

    const idToken: IDToken = {
      idToken: tokenData.idToken,
      claims: idTokenClaims,
      expiresAt: tokenData.expiresAt ?? Math.floor(Date.now() / 1000) + 3600,
      scopes,
      authorizeUrl: '',
      issuer: idTokenClaims.iss ?? '',
      clientId: idTokenClaims.aud?.toString() ?? '',
    };

    const tokens: { accessToken: AccessToken; idToken: IDToken; refreshToken?: RefreshToken } = {
      accessToken,
      idToken,
    };

    if (tokenData.refreshToken) {
      tokens.refreshToken = this.buildRefreshToken(tokenData, scopes);
    }

    return tokens;
  }

  /**
   * Extract scopes from access token claims.
   */
  private extractScopes(accessTokenClaims: Record<string, unknown>): string[] {
    if (typeof accessTokenClaims['scope'] === 'string') {
      return accessTokenClaims['scope'].split(' ').filter(Boolean);
    }
    return this.scopes();
  }

  /**
   * Build a RefreshToken object from transfer data.
   */
  private buildRefreshToken(tokenData: TokenTransferData, scopes: string[]): RefreshToken {
    const issuer = this.oktaAuth.options.issuer ?? '';
    const tokenUrl = issuer ? `${issuer}/v1/token` : '';
    const authorizeUrl = issuer ? `${issuer}/v1/authorize` : '';

    const defaultRefreshTokenLifetime = 240 * 60; // 240 minutes
    const expiresAt = tokenData.refreshTokenExpiresAt ?? Math.floor(Date.now() / 1000) + defaultRefreshTokenLifetime;

    const refreshScopes = scopes.includes('offline_access') ? scopes : [...scopes, 'offline_access'];

    return {
      refreshToken: tokenData.refreshToken ?? '',
      expiresAt,
      scopes: refreshScopes,
      authorizeUrl,
      tokenUrl,
      issuer,
    };
  }

  /**
   * Extract tokens from the URL hash fragment.
   */
  private extractTokensFromWindowUrl(): TokenTransferData | null {
    const hashFragment = this.window.location.hash;
    if (!hashFragment.startsWith('#tokens=')) {
      return null;
    }

    try {
      const tokenString = b64DecodeUnicode(hashFragment.substring(8));
      return JSON.parse(tokenString) as TokenTransferData;
    } catch {
      console.error('Failed to parse tokens from hash');
      return null;
    }
  }

  /**
   * Clear the token hash from the URL.
   */
  private clearTokenHash(): void {
    if (this.window.location.hash.startsWith('#tokens=')) {
      this.window.history.replaceState(null, '', this.window.location.pathname + this.window.location.search);
    }
  }
}
